import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useRef, useState, useMemo, useEffect } from 'react';
import { Box, Stack } from '@mui/material';
import { SMS, AssessmentData, ApplicationAccessType } from 'domains/content/types';
import ContentNameInput from 'domains/core/components/ContentNameInput';
import ErrorSnackbar from 'domains/core/components/Snackbars/ErrorSnackbar';
import useQueryParams from 'hooks/useQueryParams';
import useFeatureSettings from 'hooks/queries/useFeatureSettings';

import useFormatSmsContent from './hooks/useFormatSmsContent';
import useSmsData from './hooks/useSmsData';
import useSmsEditor from './hooks/useSmsEditor';
import useSmsForm from './hooks/useSmsForm';
import useSmsSubmission, { SmsSaveMode } from './hooks/useSmsSubmission';

import useAssessmentHandling from './hooks/useAssessmentHandling';
import useAssessmentValidation from './hooks/useAssessmentValidation';

import SmsContentWrapper from './components/SmsContentWrapper';
import SmsHeaderSection from './components/SmsHeaderSection';
import ValidationWarning from './components/ValidationWarning';
import { getFormattedSmsAutoResponses } from './components/SmsPreview/utils';
import ContentStatusText, { ContentStatus } from 'pages/ContentPage/ContentTable/ContentStatusText';

const ContentSmsBuilderPage = () => {
    // URL params
    const query = useQueryParams();
    const id = query.get('id');
    const parentFolderId = Number(query.get('parentFolderId'));
    const isCopy = query.get('isCopy') === 'true';
    // Status from URL is prioritized over API response to handle race condition when converting
    // published content to draft - the API cache might return stale "published" status before refetch completes
    const statusFromUrl = query.get('status') as ContentStatus | null;

    const [isAssessmentDialogVisible, setIsAssessmentDialogVisible] = useState(false);
    const [hasInvalidAutoResponses, setHasInvalidAutoResponses] = useState(false);
    const { contentDraft: hasContentDraft } = useFlags();
    // Get SMS data
    const { smsData, isLoading: isSavedSmsContentLoading, error: smsError, isSavedSmsContent, mergeTags } = useSmsData({
        id,
    });

    // Local status state - initialized from smsData and updated when assessment is saved
    const [localStatus, setLocalStatus] = useState<ContentStatus | undefined>(undefined);

    // Update local status when smsData changes - prioritize URL status (set when converting published to draft)
    useEffect(() => {
        if (statusFromUrl) {
            setLocalStatus(statusFromUrl);
        } else if (smsData?.status) {
            setLocalStatus(smsData.status);
        }
    }, [smsData?.status, statusFromUrl]);

    // Form state management
    const {
        applicationsAccess,
        name,
        content,
        communicationType,
        autoResponses,
        isNameUndefinedOnTouch,
        isNameTooShort,
        nameHelperText,
        errorMessage,
        setContent,
        setAutoResponses,
        setErrorMessage,
        handleChangeName,
        handleApplicationsAccessChange,
        handleCommunicationTypeChange,
    } = useSmsForm({
        initialApplicationsAccess: smsData?.applicationsAccess?.split(',') as ApplicationAccessType[],
        initialName:
            isSavedSmsContent && smsData
                ? isCopy
                    ? `Copy of ${smsData.name}`
                    : smsData.name || 'Untitled'
                : 'Untitled',
        initialCommunicationType: smsData?.communicationType,
        initialContent: isSavedSmsContent && smsData ? smsData.content || '' : '',
        initialAutoResponses: isSavedSmsContent && smsData ? smsData?.autoResponses : [],
        isSavedSmsContentLoading,
        initialStatus: smsData?.status ?? ContentStatus.DRAFT,
    });

    // Determine component mode
    const isCreate = !isCopy && !isSavedSmsContent;
    const isUpdate = !isCopy && isSavedSmsContent;

    // API operations
    const { isCreateLoading, isUpdateLoading, createError, updateError, handleSave } = useSmsSubmission({
        applicationsAccess,
        communicationType,
        id,
        parentFolderId,
        isCreate,
        isCopy,
        setErrorMessage,
    });

    // Extract initial assessment data from smsData
    const initialAssessmentData = useMemo<AssessmentData>(
        () => ({
            assessmentId: smsData?.assessmentId || '',
            assessmentName: smsData?.assessmentName || '',
            assessmentConsentRequired: smsData?.assessmentConsentRequired ?? true,
        }),
        [smsData]
    );

    // Assessment management
    const {
        assessmentData,
        setAssessmentData,
        handleSaveWithAssessment,
        handleSaveAndExitWithAssessment,
    } = useAssessmentHandling({
        initialAssessmentData,
        isNameTooShort,
        isUpdate,
        handleSave,
    });

    const combinedSmsData = useMemo<SMS>(
        () => ({
            ...smsData,
            ...assessmentData,
            ...autoResponses,
            name,
            content,
        }),
        [smsData, assessmentData, name, content, autoResponses]
    );

    const { data: featureSettings } = useFeatureSettings();

    const isAssessmentEnabled = !!featureSettings?.assessments_enabled;

    // Assessment validation
    const {
        warningMessage,
        warningType,
        validateAndSaveExit,
        handleWarningClose,
        handleRemoveAssessment,
        handleSelectAssessment,
    } = useAssessmentValidation({
        content,
        combinedSmsData,
        isAssessmentEnabled,
        handleSaveAndExit: (status?: ContentStatus) => {
            if (hasContentDraft) {
                handleSaveAndExitWithAssessment({
                    name,
                    content,
                    autoResponses,
                    status: status ?? ContentStatus.DRAFT,
                });
            } else {
                handleSaveAndExitWithAssessment({ name, content, autoResponses });
            }
        },
        setSmsData: setAssessmentData,
        setIsAssessmentDialogVisible,
    });

    // Editor reference for cursor management
    const smsEditorRef = useRef(null);

    // Assessment tag insertion helper
    const { insertTagIntoContent } = useSmsEditor(smsEditorRef);

    const handleAssessmentSelected = useCallback(
        (assessmentId: string, assessmentName: string, isConsentRequired: boolean, tagToInsert: string) => {
            const updatedContent = insertTagIntoContent(content, tagToInsert);

            const newAssessmentData = {
                assessmentId,
                assessmentName,
                assessmentConsentRequired: isConsentRequired,
            };

            setContent(updatedContent);
            setAssessmentData(newAssessmentData);

            // When assessment is added, set status to DRAFT immediately in local state
            if (hasContentDraft) {
                setLocalStatus(ContentStatus.DRAFT);
            }

            // When assessment is added, set status to DRAFT
            handleSaveWithAssessment({
                name,
                content: updatedContent,
                autoResponses,
                saveMode: SmsSaveMode.SAVE,
                overrideAssessmentData: newAssessmentData,
                ...(hasContentDraft && { status: ContentStatus.DRAFT }),
            });
        },
        [
            insertTagIntoContent,
            content,
            setContent,
            setAssessmentData,
            handleSaveWithAssessment,
            name,
            autoResponses,
            hasContentDraft,
        ]
    );

    const handleUpdateSmsWithAssessment = useCallback(
        (updates: Partial<SMS>) => {
            // Update assessment metadata
            let newAssessmentData = { ...assessmentData };
            if ('assessmentId' in updates || 'assessmentName' in updates || 'assessmentConsentRequired' in updates) {
                newAssessmentData = {
                    assessmentId: updates.assessmentId || '',
                    assessmentName: updates.assessmentName || '',
                    assessmentConsentRequired: updates.assessmentConsentRequired ?? true,
                };
                setAssessmentData(newAssessmentData);
            }

            // Update SMS content if provided
            let newContent = content;
            if ('content' in updates && typeof updates.content === 'string') {
                newContent = updates.content;
                setContent(newContent);
            }
            handleSaveWithAssessment({
                name,
                content: newContent,
                autoResponses,
                saveMode: SmsSaveMode.SAVE,
                overrideAssessmentData: newAssessmentData,
            });
        },
        [assessmentData, content, handleSaveWithAssessment, name, autoResponses, setAssessmentData, setContent]
    );

    // Format content for preview
    const formattedPreviewContent = useFormatSmsContent(content, mergeTags);
    const formattedSmsAutoResponses = autoResponses ? getFormattedSmsAutoResponses(autoResponses, mergeTags) : null;

    // Loading and error states
    const isLoading = isSavedSmsContentLoading || isCreateLoading || isUpdateLoading;
    const isError = !!smsError || !!createError || !!updateError || !!errorMessage;
    const errorDisplayMessage = errorMessage || smsError?.message || createError?.message || updateError?.message;

    return (
        <Stack height="100%" mb={4} rowGap={4}>
            <ValidationWarning
                warningMessage={warningMessage}
                warningType={warningType}
                onClose={handleWarningClose}
                onRemoveAssessment={handleRemoveAssessment}
                onSelectAssessment={handleSelectAssessment}
            />
            {isError && <ErrorSnackbar errorMessage={errorDisplayMessage} />}
            <Box sx={{ display: 'inline-flex', alignItems: 'flex-start', gap: 2 }}>
                <ContentNameInput
                    dataTestId="name-input"
                    disabled={id !== 'default' && id !== null ? isSavedSmsContentLoading : false}
                    error={isNameUndefinedOnTouch || isNameTooShort}
                    helperText={nameHelperText}
                    name={name}
                    onChange={handleChangeName}
                    withClickAway
                />
                {hasContentDraft && (
                    <Box sx={{ pt: 3 }}>
                        <ContentStatusText
                            status={
                                id && id !== 'default' && !isCopy
                                    ? localStatus || (smsData as any)?.status || ' '
                                    : ContentStatus.DRAFT
                            }
                        />
                    </Box>
                )}
            </Box>

            <SmsHeaderSection
                applicationsAccess={applicationsAccess}
                communicationType={communicationType}
                handleApplicationsAccess={handleApplicationsAccessChange}
                handleCommunicationType={handleCommunicationTypeChange}
                hasInvalidAutoResponses={hasInvalidAutoResponses}
                isAssessmentDialogVisible={isAssessmentDialogVisible}
                isAssessmentEnabled={isAssessmentEnabled}
                isLoading={isLoading}
                onAssessmentSelected={handleAssessmentSelected}
                onSaveAndExit={validateAndSaveExit}
                setIsAssessmentDialogVisible={setIsAssessmentDialogVisible}
                setSmsData={handleUpdateSmsWithAssessment}
                smsData={combinedSmsData}
            />
            <SmsContentWrapper
                autoResponses={autoResponses}
                content={content}
                editorRef={smsEditorRef}
                formattedSmsAutoResponses={formattedSmsAutoResponses}
                formattedPreviewContent={formattedPreviewContent}
                setAutoResponses={setAutoResponses}
                setContent={setContent}
                setHasInvalidAutoResponses={setHasInvalidAutoResponses}
            />
        </Stack>
    );
};

export default ContentSmsBuilderPage;
